import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Clapperboard, Sparkles, Play, Download, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { AssistantChat } from "../components/AssistantChat";
import VideoPlayerDialog from "../components/VideoPlayerDialog";
import { iosSave } from "../lib/iosSave";
import { errMsg } from "../lib/errMsg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONES = [
  { id: "professional", label: "Professional" },
  { id: "casual", label: "Casual" },
  { id: "inspiring", label: "Inspiring" },
];
const DURATIONS = [10, 15, 20, 30];
const ASPECTS = [
  { id: "16:9", label: "Landscape" },
  { id: "9:16", label: "Story / Reel" },
  { id: "1:1", label: "Square" },
];

const estimateCost = (duration) => Math.ceil(duration / 10) * 10;

const ExplainerPage = () => {
  const { token, refreshUser } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };

  const [description, setDescription] = useState("");
  const [tone, setTone] = useState("professional");
  const [duration, setDuration] = useState(20);
  const [aspect, setAspect] = useState("16:9");

  const [scripting, setScripting] = useState(false);
  const [script, setScript] = useState(null); // {video_prompt, negative_prompt, headline}
  const [starting, setStarting] = useState(false);
  const [task, setTask] = useState(null);     // video task status doc
  const [playerOpen, setPlayerOpen] = useState(false);
  const pollRef = useRef(null);

  // Resume an in-flight explainer render after navigation
  useEffect(() => {
    const saved = localStorage.getItem("xea-explainer-task-id");
    if (saved && token) pollStatus(saved);
    return () => clearTimeout(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const pollStatus = async (taskId) => {
    try {
      const r = await axios.get(`${API}/video-gen/status/${taskId}`, { headers: auth });
      setTask({ ...r.data, task_id: taskId });
      const done = r.data.status === "completed" || r.data.status === "failed";
      const polishing = r.data.polish_status === "processing";
      if (!done || polishing) {
        pollRef.current = setTimeout(() => pollStatus(taskId), 5000);
      } else if (r.data.status === "failed") {
        localStorage.removeItem("xea-explainer-task-id");
      }
    } catch (_e) {
      localStorage.removeItem("xea-explainer-task-id");
    }
  };

  const handleScript = async () => {
    if (description.trim().length < 10) {
      toast.error("Describe the company first — a couple of sentences is enough.");
      return;
    }
    setScripting(true);
    try {
      const r = await axios.post(
        `${API}/explainer/script`,
        { description: description.trim(), tone, duration },
        { headers: auth }
      );
      setScript(r.data);
      toast.success("Script ready — review it, then generate the video.");
    } catch (e) {
      toast.error(errMsg(e, "Could not write the script."));
    } finally {
      setScripting(false);
    }
  };

  const handleGenerate = async () => {
    if (!script?.video_prompt) return;
    setStarting(true);
    try {
      const r = await axios.post(
        `${API}/video-gen/text-to-video`,
        {
          prompt: script.video_prompt,
          negative_prompt: script.negative_prompt,
          duration,
          aspect_ratio: aspect,
          music_choice: "auto",
        },
        { headers: auth }
      );
      localStorage.setItem("xea-explainer-task-id", r.data.task_id);
      toast.success("Explainer rendering! Safe to leave — it also shows in the Render Queue.");
      pollStatus(r.data.task_id);
      refreshUser?.();
    } catch (e) {
      toast.error(errMsg(e, "Could not start the video."));
    } finally {
      setStarting(false);
    }
  };

  const reset = () => {
    clearTimeout(pollRef.current);
    localStorage.removeItem("xea-explainer-task-id");
    setScript(null);
    setTask(null);
  };

  const finalUrl = task?.polished_url || task?.video_url;
  const inFlight = task && (task.status === "processing" || task.polish_status === "processing");
  const cost = estimateCost(duration);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8" data-testid="explainer-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-blue-500 flex items-center justify-center shadow-md">
          <Clapperboard className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-500">
            Company Explainer
          </p>
          <h1 className="text-3xl md:text-4xl font-black text-ink-950 tracking-tight">
            Show the world what you do.
          </h1>
        </div>
      </div>

      {/* Step 1 — brief */}
      <div className="bg-white border border-ink-200 rounded-3xl p-6 space-y-5">
        {/* AI assistant — helps draft the company description */}
        <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50/40 to-white p-3">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-600 mb-2 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Ask XEA to help describe the company
          </p>
          <AssistantChat
            variant="embed"
            contextHint="Tell me what the company does and who it's for — I'll shape it into a description the director can work with."
            usePromptLabel="Use as company description"
            onUsePrompt={(text) => {
              setDescription(text.trim().slice(0, 3000));
              toast.success("Description copied into the form below");
            }}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">
            Step 1 · Describe the company
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={3000}
            placeholder="We build handmade ceramic tableware in Athens and ship across Europe..."
            data-testid="explainer-description"
            className="w-full p-3 rounded-xl border border-ink-200 focus:outline-none focus:border-blue-500 text-sm resize-none"
          />
          <p className="text-[11px] text-ink-500 mt-1">
            💡 Products and store URLs from your Marketing realm are woven in automatically.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">Tone</label>
            <div className="flex flex-wrap gap-1.5">
              {TONES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTone(t.id)}
                  data-testid={`explainer-tone-${t.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                    tone === t.id
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white text-ink-700 border-ink-200 hover:border-blue-500"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">Duration</label>
            <div className="flex flex-wrap gap-1.5">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDuration(d)}
                  data-testid={`explainer-duration-${d}`}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition ${
                    duration === d
                      ? "bg-ink-950 text-white border-ink-950"
                      : "bg-white text-ink-700 border-ink-200 hover:border-ink-950"
                  }`}
                >
                  {d}s
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">Format</label>
            <div className="flex flex-wrap gap-1.5">
              {ASPECTS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setAspect(a.id)}
                  data-testid={`explainer-aspect-${a.id}`}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition ${
                    aspect === a.id
                      ? "bg-ink-950 text-white border-ink-950"
                      : "bg-white text-ink-700 border-ink-200 hover:border-ink-950"
                  }`}
                >
                  {a.id} <span className="font-normal opacity-70">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <Button
          onClick={handleScript}
          disabled={scripting || description.trim().length < 10}
          data-testid="explainer-script-btn"
          className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white"
        >
          {scripting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {scripting ? "Directing…" : script ? "Rewrite script" : "Write my script · free"}
        </Button>
      </div>

      {/* Step 2 — script review + generate */}
      {script && (
        <div className="bg-white border border-blue-200 rounded-3xl p-6 space-y-4" data-testid="explainer-script-panel">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-500">
              Step 2 · Your director's script
            </p>
            {script.headline && (
              <p className="text-lg font-black text-ink-950 mt-1">“{script.headline}”</p>
            )}
          </div>
          <textarea
            value={script.video_prompt}
            onChange={(e) => setScript((s) => ({ ...s, video_prompt: e.target.value }))}
            rows={7}
            maxLength={2000}
            data-testid="explainer-script-text"
            className="w-full p-3 rounded-xl border border-ink-200 focus:outline-none focus:border-blue-500 text-sm resize-none font-mono"
          />
          <p className="text-[11px] text-ink-500">Feel free to tweak it — this is exactly what the video engine will render.</p>
          <Button
            onClick={handleGenerate}
            disabled={starting || !!inFlight}
            data-testid="explainer-generate-btn"
            className="w-full sm:w-auto bg-ink-950 hover:bg-ink-800 text-white"
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />}
            {starting ? "Starting…" : `Generate explainer · ~${cost} XEA`}
          </Button>
        </div>
      )}

      {/* Step 3 — render status / result */}
      {task && (
        <div className="bg-white border border-ink-200 rounded-3xl p-6 space-y-4" data-testid="explainer-status-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
              Step 3 · Your explainer
            </p>
            <span
              className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full ${
                task.status === "completed" && !inFlight
                  ? "bg-emerald-100 text-emerald-700"
                  : task.status === "failed"
                  ? "bg-rose-100 text-rose-700"
                  : "bg-amber-100 text-amber-700"
              }`}
              data-testid="explainer-status-badge"
            >
              {inFlight ? "rendering" : task.status}
            </span>
          </div>

          {inFlight && (
            <div className="flex items-center gap-3 text-sm text-ink-600">
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              <span>
                {task.progress || "Rendering your explainer…"}
                {" — safe to leave this page, it keeps rendering."}
              </span>
            </div>
          )}

          {task.status === "failed" && (
            <p className="text-sm text-rose-600">{task.error || "Render failed — tokens were auto-refunded."}</p>
          )}

          {task.status === "completed" && !inFlight && finalUrl && (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={() => setPlayerOpen(true)}
                data-testid="explainer-play-btn"
              >
                <Play className="w-3.5 h-3.5 mr-1.5" /> Play
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => iosSave(finalUrl, { kind: "video", filename: `xea-explainer-${task.task_id?.slice(0, 8)}.mp4` })}
                data-testid="explainer-download-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download
              </Button>
              <Button size="sm" variant="ghost" onClick={reset} data-testid="explainer-reset-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Make another
              </Button>
            </div>
          )}
        </div>
      )}

      <VideoPlayerDialog
        open={playerOpen}
        onClose={() => setPlayerOpen(false)}
        url={finalUrl}
        title="Company explainer"
      />
    </div>
  );
};

export default ExplainerPage;
